import React, { useMemo } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-solid-svg-icons/faPlus';
import { TypedMemo } from '../../../../../common/types';
import { SMLButton } from '../../../../components/primitives/SMLButton';
import { StudioActionError } from '../../../studio/StudioActionError';
import { planDayDate, planDayTitle } from '../rolloutDayCopy';
import { ChannelMark } from './ChannelMark';
import { useRollout } from './RolloutContext';
import {
  barShares,
  nextScheduledPost,
  platformOfPlanPosts,
  type FitResult,
  type PlanCounts,
  type PlanStepView,
  unplannedStepCounts,
} from './rolloutModel';
import { autoPostPlatforms, postingSplitOf } from './launch/postingModes';
import { launchMessages } from './launch/launchMessages';
import { rolloutMessages } from './rolloutMessages';
import { dayAndHourInZone } from './rolloutTime';
import { useSMLRouter } from '../../../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../../../routing/sml/SMLRoutes';
import { WatermarkPostWarning } from '../../../../watermarkUpsell/files/WatermarkPostWarning';
import { watermarkedPlanDrafts } from '../../../../watermarkUpsell/files/planWatermarkCount';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the run sheet; each part uses its own slice of it */
import styles from './Rollout.module.scss';
/* eslint-enable css-modules/no-unused-class */

const PART_CLASS = {
  ok: styles.partOk,
  sch: styles.partSch,
  dr: styles.partDr,
  bad: styles.partBad,
  miss: styles.partMiss,
} as const;

interface RolloutSummaryProps {
  steps: PlanStepView[];
  counts: PlanCounts;
  needsCount: number;
  fit: FitResult;
  /** The channels line under the summary. */
  children: React.ReactNode;
}

/**
 * The summary of the plan (design rollout-plan a1, b1, e1 `.rp-sum`): one count and one bar toward
 * release day, the key, and one action: "Plan all N posts" (drafts, owner decision 1), "Schedule all
 * N posts" (one step: the server schedules what can go out, decision 8 and the launch of
 * 2026-10-10), or the next post. With no connected channel (QA finding F02, decision D5) the main
 * action is "Connect a channel" and planning stays a second action: the posts then come as "post
 * it yourself" reminders. The line under "Schedule all" says how many posts go out by themselves.
 */
const RolloutSummaryComponent: React.FC<RolloutSummaryProps> = ({ steps, counts, needsCount, fit, children }) => {
  const intl = useIntl();
  const { actions, canAct, channels, timeZone, release } = useRollout();
  // design: trial-upsell a5. The drafts that "Schedule all" sends out with a watermarked file.
  const watermarkedDrafts = useMemo(() => watermarkedPlanDrafts(steps, release), [steps, release]);
  const nothingYet = counts.posted + counts.scheduled + counts.drafts + counts.failed === 0;
  const visible = steps.filter((step) => step.day.skipped !== true);
  const fitting = fit.drafts.filter((draft) => draft.fits);
  const next = nextScheduledPost(steps, new Date());
  const busy = actions.busy !== null;
  const noChannel = channels.length === 0;
  const router = useSMLRouter();
  const autoPlatforms = useMemo(() => autoPostPlatforms(channels), [channels]);
  const noAutoChannel = autoPlatforms.size === 0;
  const split = useMemo(
    () =>
      postingSplitOf(
        fit.drafts.map((draft) => draft.postUuid),
        platformOfPlanPosts(steps),
        autoPlatforms
      ),
    [autoPlatforms, fit.drafts, steps]
  );
  const connect = noAutoChannel ? (
    <SMLButton variant="primary" disabled={!canAct} onClick={() => router.push(SMLRoutes.promoteChannels)}>
      {intl.formatMessage(rolloutMessages.connectChannel)}
    </SMLButton>
  ) : null;
  const planVariant = noAutoChannel ? 'default' : 'primary';

  // QA 2026-10-28 (imp-2): with no channel and no handle a step has no unit, so the counts are 0.
  // The summary then counts the steps and names the real blocker, with the action as the main button.
  const stepCounts = useMemo(() => unplannedStepCounts(steps), [steps]);
  const noPlace = noChannel && nothingYet;

  let action: React.ReactNode = null;
  if (noPlace) {
    action = (
      <SMLButton variant="primary" disabled={!canAct} onClick={() => router.push(SMLRoutes.promoteChannels)}>
        {intl.formatMessage(rolloutMessages.addWherePost)}
      </SMLButton>
    );
  } else if (counts.readyToPlan > 0) {
    action = (
      <>
        {connect}
        <SMLButton
          variant={planVariant}
          icon={<FontAwesomeIcon icon={faPlus} />}
          disabled={!canAct || busy || noChannel}
          onClick={() => {
            actions.planDays('plan').catch(console.error);
          }}
        >
          {intl.formatMessage(rolloutMessages.planAll, { count: counts.readyToPlan })}
        </SMLButton>
        <span className={styles.cost}>
          {intl.formatMessage(rolloutMessages.planAllCost, { count: counts.readyToPlan })}
        </span>
      </>
    );
  } else if (counts.drafts > 0) {
    const left = fit.drafts.length - fitting.length;
    // Every draft goes to the server: it schedules what can go out and names the rest (a month
    // limit, a signed-out channel), so the summary under this panel has one row for each of them.
    action = (
      <>
        {connect}
        <SMLButton
          variant={planVariant}
          disabled={!canAct || busy}
          onClick={() => {
            actions
              .launch(
                'plan',
                fit.drafts.map((draft) => draft.postUuid)
              )
              .catch(console.error);
          }}
        >
          {intl.formatMessage(launchMessages.scheduleAll, { count: counts.drafts })}
        </SMLButton>
        <span className={styles.cost}>
          {left > 0
            ? intl.formatMessage(rolloutMessages.scheduleAllFit, { fits: fitting.length, left })
            : intl.formatMessage(rolloutMessages.scheduleAllUses, { count: fitting.length })}
        </span>
        <span className={styles.cost} data-testid="schedule-split">
          {intl.formatMessage(rolloutMessages.scheduleSplit, { auto: split.auto, manual: split.manual })}
        </span>
      </>
    );
  } else if (next !== null) {
    action = (
      <>
        {connect}
        <span className={styles.eyebrow}>{intl.formatMessage(rolloutMessages.nextPost)}</span>
        <span className={styles.next}>
          <b>{dayAndHourInZone(intl, next.channel.at, timeZone)}</b> · {planDayTitle(intl, next.step.day)}
        </span>
        <span className={styles.chns}>
          <ChannelMark platform={next.channel.platform} small />
        </span>
      </>
    );
  }

  const firstStep = visible[0];
  const lastStep = visible[visible.length - 1];
  const lead = nothingYet
    ? intl.formatMessage(rolloutMessages.emptyLead, {
        steps: visible.length,
        first: firstStep === undefined ? '' : planDayDate(intl, firstStep.date),
        last: lastStep === undefined ? '' : planDayDate(intl, lastStep.date),
      })
    : intl.formatMessage(needsCount === 0 ? rolloutMessages.onTrack : rolloutMessages.needsCount, {
        count: needsCount,
      });
  let body: string;
  if (noPlace) {
    body = intl.formatMessage(rolloutMessages.noChannelsBody, { ready: stepCounts.ready });
  } else if (nothingYet) {
    body = intl.formatMessage(rolloutMessages.emptyBody, { ready: counts.readyToPlan, missing: counts.needAsset });
  } else {
    body = intl.formatMessage(rolloutMessages.waitingCount, { count: counts.scheduled });
  }
  let countLabel = intl.formatMessage(rolloutMessages.countOut, { total: counts.total });
  if (noPlace) {
    countLabel = intl.formatMessage(rolloutMessages.countPlannedSteps, { total: visible.length });
  } else if (nothingYet) {
    countLabel = intl.formatMessage(rolloutMessages.countPlanned, { total: counts.total });
  }

  return (
    <>
      <div className={styles.sum}>
        <div className={styles.big}>
          <span className={styles.eyebrow}>{intl.formatMessage(rolloutMessages.eyebrow)}</span>
          <b>
            {nothingYet ? 0 : counts.posted}
            {/* A real space, so the text reads "0 of 3", not "0of 3" (QA E). */}
            <small> {countLabel}</small>
          </b>
        </div>
        <div className={styles.mid}>
          <p>
            <b>{lead}</b> {body}
          </p>
          <div className={styles.bar}>
            {barShares(counts).map(({ part, share }) => (
              <i key={part} className={PART_CLASS[part]} style={{ width: `${share}%` }} />
            ))}
          </div>
          <SummaryKey counts={counts} nothingYet={nothingYet} />
        </div>
        <div className={styles.act}>
          {action}
          <StudioActionError feedback={actions.feedback.plan ?? null} />
        </div>
      </div>
      {counts.drafts > 0 && <WatermarkPostWarning variant="plan" count={watermarkedDrafts} />}
      {children}
    </>
  );
};

export const RolloutSummary = TypedMemo(RolloutSummaryComponent);

/** The key under the bar: only the parts that have a count. */
const SummaryKeyComponent: React.FC<{ counts: PlanCounts; nothingYet: boolean }> = ({ counts, nothingYet }) => {
  const intl = useIntl();
  const parts: Array<[number, string | undefined, string]> = [
    [counts.posted, styles.partOk, intl.formatMessage(rolloutMessages.keyPosted)],
    [counts.scheduled, styles.partSch, intl.formatMessage(rolloutMessages.keyScheduled)],
    [counts.drafts, styles.keyDr, intl.formatMessage(rolloutMessages.keyDraft, { count: counts.drafts })],
    [counts.readyToPlan, styles.keyDr, intl.formatMessage(rolloutMessages.keyReady)],
    [counts.failed, styles.partBad, intl.formatMessage(rolloutMessages.keyFailed)],
    [counts.needAsset, styles.keyMiss, intl.formatMessage(rolloutMessages.keyNeedAsset)],
  ];
  return (
    <div className={styles.key}>
      {parts
        .filter(([count]) => count > 0)
        .map(([count, className, label]) => (
          <span key={label}>
            <i className={clsx(className)} />
            <b>{count}</b>
            {label}
          </span>
        ))}
      {nothingYet && <span>{intl.formatMessage(rolloutMessages.keyNothingOut)}</span>}
    </div>
  );
};

const SummaryKey = TypedMemo(SummaryKeyComponent);
